import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { modelsService } from '../services/models';

// Async thunks
export const fetchModels = createAsyncThunk(
  'models/fetchModels',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await modelsService.getModels(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch models');
    }
  }
);

export const fetchModel = createAsyncThunk(
  'models/fetchModel',
  async (code, { rejectWithValue }) => {
    try {
      const response = await modelsService.getModel(code);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch model');
    }
  }
);

export const createModel = createAsyncThunk(
  'models/createModel',
  async (modelData, { rejectWithValue }) => {
    try {
      const response = await modelsService.createModel(modelData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create model');
    }
  }
);

export const updateModel = createAsyncThunk(
  'models/updateModel',
  async ({ code, modelData }, { rejectWithValue }) => {
    try {
      const response = await modelsService.updateModel(code, modelData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update model');
    }
  }
);

export const deleteModel = createAsyncThunk(
  'models/deleteModel',
  async (code, { rejectWithValue }) => {
    try {
      await modelsService.deleteModel(code);
      return code;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete model');
    }
  }
);

export const exportModels = createAsyncThunk(
  'models/exportModels',
  async (params = {}, { rejectWithValue }) => {
    try {
      const blob = await modelsService.exportModels(params);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_');
      const source = params.source || 'all';
      const filename = `ir_models_${source}_${timestamp}.csv`;
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export models');
    }
  }
);

const initialState = {
  models: [],
  currentModel: null,
  pagination: null,
  loading: false,
  error: null,
  searchTerm: '',
  filters: {
    brand: '',
    device_type: '',
    source: '',
  },
};

const modelsSlice = createSlice({
  name: 'models',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.searchTerm = '';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch models
      .addCase(fetchModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.loading = false;
        state.models = action.payload.models;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single model
      .addCase(fetchModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModel.fulfilled, (state, action) => {
        state.loading = false;
        state.currentModel = action.payload.model;
        state.error = null;
      })
      .addCase(fetchModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create model
      .addCase(createModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createModel.fulfilled, (state, action) => {
        state.loading = false;
        state.models.unshift(action.payload.model);
        state.error = null;
      })
      .addCase(createModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update model
      .addCase(updateModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateModel.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.models.findIndex(m => m.code === action.payload.model.code);
        if (index !== -1) {
          state.models[index] = action.payload.model;
        }
        state.error = null;
      })
      .addCase(updateModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete model
      .addCase(deleteModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteModel.fulfilled, (state, action) => {
        state.loading = false;
        state.models = state.models.filter(m => m.code !== action.payload);
        state.error = null;
      })
      .addCase(deleteModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Export models
      .addCase(exportModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportModels.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(exportModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setSearchTerm, setFilters, clearFilters } = modelsSlice.actions;
export default modelsSlice.reducer;
